import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const EMPTY = { name: "", description: "", hsn: "", unit: "Pcs", gst_pct: 18, default_rate: 0 };

export function AdminItems({ token, onUnauthorized }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/admin/items`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.status === 401) return onUnauthorized();
    setItems(await r.json());
  }, [token, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      const isNew = editing === "new";
      const url = isNew ? `${API_BASE}/api/admin/items` : `${API_BASE}/api/admin/items/${editing.id}`;
      const method = isNew ? "POST" : "PUT";
      const payload = { ...form, gst_pct: Number(form.gst_pct) || 0, default_rate: Number(form.default_rate) || 0 };
      const r = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      setEditing(null);
      await load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!confirm(`Delete item "${it.name}"?`)) return;
    const r = await fetch(`${API_BASE}/api/admin/items/${it.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) load();
  };

  const filtered = (items || []).filter((it) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (it.name + " " + it.hsn + " " + it.description).toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-items">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, HSN…" className="pl-9" data-testid="item-search" />
        </div>
        <Button onClick={() => setEditing("new")} data-testid="item-new-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
          <Plus className="h-4 w-4 mr-1" /> New Item
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto" data-testid="items-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Item Name</th>
              <th className="px-4 py-3 font-semibold">HSN</th>
              <th className="px-4 py-3 font-semibold">Unit</th>
              <th className="px-4 py-3 font-semibold text-right">Default Rate</th>
              <th className="px-4 py-3 font-semibold text-right">GST%</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!items && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {items && filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No items yet. Add items you frequently order to speed up PO creation.</td></tr>}
            {filtered.map((it) => (
              <tr key={it.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#0a2a5e]">{it.name}</div>
                  {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{it.hsn || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{it.unit}</td>
                <td className="px-4 py-3 text-right text-slate-600">{Number(it.default_rate).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-slate-600">{Number(it.gst_pct).toFixed(0)}%</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(it)} data-testid={`item-edit-${it.id}`} className="text-sky-600 hover:text-sky-800 p-1.5"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(it)} data-testid={`item-del-${it.id}`} className="text-red-500 hover:text-red-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <ItemForm initial={editing === "new" ? EMPTY : editing} onCancel={() => setEditing(null)} onSave={save} saving={saving} />}
    </div>
  );
}

function ItemForm({ initial, onCancel, onSave, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); if (!form.name.trim()) return alert("Name is required"); onSave(form); };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="item-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-[#0a2a5e]">{initial.id ? "Edit Item" : "New Item"}</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Item Name *</label>
            <Input value={form.name} onChange={set("name")} required data-testid="item-name-input" placeholder="e.g. PVC Resin K-67" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
            <Input value={form.description} onChange={set("description")} placeholder="e.g. Suspension grade, IS 10151 compliant" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">HSN Code</label>
              <Input value={form.hsn} onChange={set("hsn")} placeholder="e.g. 39041020" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Unit</label>
              <Input value={form.unit} onChange={set("unit")} placeholder="Pcs / Kg / Meter" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Default Rate (₹)</label>
              <Input type="number" step="any" value={form.default_rate} onChange={set("default_rate")} data-testid="item-rate-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">GST %</label>
              <Input type="number" step="any" value={form.gst_pct} onChange={set("gst_pct")} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="item-save-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
              {saving ? "Saving…" : "Save Item"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
